import { create } from 'zustand'
import type { Role } from '../store/role'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  variant: ToastVariant
  title: string
  description?: string
  audience?: Role | Role[]
  created_at: number
  duration_ms: number
}

type ToastInput = Omit<Toast, 'id' | 'created_at' | 'duration_ms'> & {
  duration_ms?: number
}

type ToastStore = {
  toasts: Toast[]
  push: (input: ToastInput) => string
  dismiss: (id: string) => void
  clear: () => void
}

let _seq = 0
function nextId() {
  _seq += 1
  return `t-${Date.now()}-${_seq}`
}

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (input) => {
    const id = nextId()
    const toast: Toast = {
      id,
      variant: input.variant,
      title: input.title,
      description: input.description,
      audience: input.audience,
      created_at: Date.now(),
      duration_ms: input.duration_ms ?? 5000,
    }
    set((s) => ({ toasts: [...s.toasts, toast] }))
    return id
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}))

export const toast = {
  success: (title: string, opts?: Partial<ToastInput>) =>
    useToastStore.getState().push({ ...opts, variant: 'success', title }),
  error: (title: string, opts?: Partial<ToastInput>) =>
    useToastStore.getState().push({ ...opts, variant: 'error', title }),
  warning: (title: string, opts?: Partial<ToastInput>) =>
    useToastStore.getState().push({ ...opts, variant: 'warning', title }),
  info: (title: string, opts?: Partial<ToastInput>) =>
    useToastStore.getState().push({ ...opts, variant: 'info', title }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
  clear: () => useToastStore.getState().clear(),
}

export function isVisibleToRole(t: Toast, role: Role | ''): boolean {
  if (!t.audience) return true
  if (!role) return false
  if (Array.isArray(t.audience)) return t.audience.includes(role)
  return t.audience === role
}
